// All landing page copy. Change the words here, not in the JSX.
import React from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

import { TintName } from './landingTokens';

export type IconItem = {
  title: string;
  description?: string;
  icon: React.ElementType;
  tint: TintName;
  kicker?: string;
};

export const hero = {
  steps: [
    { verb: 'Plan', note: 'the day' },
    { verb: 'Review', note: 'before dispatch' },
    { verb: 'Track', note: 'every trip' },
  ],
  titleLine1: 'Last-mile routing built around your operation.',
  titleLine2: 'Not the other way around.',
  lede: 'ExPlan plans optimised delivery routes around your windows, fleet and depots, lets planners review every change, and tracks each vehicle against the plan through the day.',
  caption: '20-minute walkthrough · No commitment',
};

// Scrolling list under the benefit strip: the industries ExPlan is built for.
// Sectors, not client names.
export const operations = [
  'E-commerce and parcels',
  'Grocery and quick commerce',
  'FMCG distribution',
  'Pharmaceutical distribution',
  'Food and beverage',
  'Furniture and appliances',
  'Waste and recycling',
  'Third-party logistics',
];

// The strip directly under the hero: outcomes, not features.
export const benefits: IconItem[] = [
  {
    title: 'Fewer wasted kilometres',
    description: 'Routes built on real roads, delivery windows and vehicle limits, not straight-line estimates.',
    icon: RouteRoundedIcon,
    tint: 'blue',
  },
  {
    title: 'More deliveries on time',
    description: 'Every stop planned inside its window, with risks flagged before vehicles leave.',
    icon: AccessTimeRoundedIcon,
    tint: 'green',
  },
  {
    title: 'Faster daily planning',
    description: 'Plans ready in minutes, so planners spend their time reviewing, not building routes by hand.',
    icon: BoltRoundedIcon,
    tint: 'amber',
  },
  {
    title: 'Fits your operation',
    description: 'Your rules, fleet and depots modelled the way you run them, not forced into a template.',
    icon: TuneRoundedIcon,
    tint: 'purple',
  },
];

// "The gap" (GapSection.tsx): a straight plan line and a road line that steps
// further away at four points. `mark` labels the point on the drawing.
export type GapItem = {
  mark: string;
  title: string;
  description: string;
};

export const gap = {
  title: 'Where last-mile plans quietly break',
  items: [
    {
      mark: '+ generic rules',
      title: 'Generic tools, approximate rules',
      description:
        "Standard routing software can't capture your delivery windows, fleet mix or depot rules, so planners fix the gaps by hand.",
    },
    {
      mark: '+ bad data',
      title: 'Data errors reach the road',
      description: 'An incorrect address or missing vehicle detail rarely stops the plan. It produces routes that fail in the field.',
    },
    {
      mark: '+ lost know-how',
      title: 'Local knowledge is left out',
      description: 'Experienced planners know which customers need special handling. Automated plans have no way to capture it.',
    },
    {
      mark: '+ no visibility',
      title: 'No visibility after dispatch',
      description: 'Without live tracking against the plan, delays and failed deliveries surface only at the end of the day.',
    },
  ] as GapItem[],
};

// "The solution" (SolutionSection.tsx): a sticky index on the left and five
// parts on the right, covering the day from model to multi-depot management.
export type SolutionPart = {
  id: string;
  title: string;
  body: string;
  facts: string[];
};

export const solution = {
  title: 'Everything you need to plan, run and review the delivery day.',
  parts: [
    {
      id: 'solution-model',
      title: 'Custom routing model',
      body: 'A routing model configured around your network, with your delivery windows, fleet and depot rules, rather than a generic template.',
      facts: ['Your rules, modelled as you run them', 'Developed with your planning team', 'Refined as your operation changes'],
    },
    {
      id: 'solution-upload',
      title: 'Order upload and validation',
      body: "Bring in the day's orders from a file. Every row is checked before planning starts, so data issues are caught early instead of on the road.",
      facts: ['Clear, row-level error messages', 'Planning starts only on clean data'],
    },
    {
      id: 'solution-review',
      title: 'Planner review',
      body: 'Planners stay in control. Review the proposed plan, move stops between routes and re-optimise in seconds.',
      facts: ['Edit routes on the map', 'Correct locations in place', 'Compare against the original plan', 'Re-optimise when volumes change'],
    },
    {
      id: 'solution-tracking',
      title: 'Live tracking',
      body: 'Follow every vehicle against the approved plan. Delays and exceptions are visible while there is still time to act.',
      facts: ['Planned vs actual arrival for every stop', 'Returns and missed stops flagged in real time'],
    },
    {
      id: 'solution-depots',
      title: 'Multi-depot management',
      body: 'Run several depots from one platform, with plans, users and permissions organised by depot.',
      facts: ['Depot-level plans and views', 'Role-based access for planning teams'],
    },
  ] as SolutionPart[],
};

// "Engine capabilities" (CapabilitySection.tsx): what the routing engine handles.
// General descriptions for the demo site; confirm each against the live models
// before using the page with real customers.
export const capabilities = {
  title: 'An engine that plans the way you operate',
  items: [
    {
      title: 'Delivery time windows',
      description: 'Every stop carries its own window. Plans respect it, and flag any that cannot be met.',
      icon: AccessTimeRoundedIcon,
      tint: 'blue',
    },
    {
      title: 'Mixed fleets and capacity',
      description: 'Vans, bikes and trucks, each with its own limits by weight, volume or item count.',
      icon: LocalShippingRoundedIcon,
      tint: 'purple',
    },
    {
      title: 'Multiple depots',
      description: 'Plan several depots in one run, with each vehicle starting and ending where it should.',
      icon: WarehouseRoundedIcon,
      tint: 'amber',
    },
    {
      title: 'Shifts and working hours',
      description: 'Driver shifts, breaks and maximum route times built into every trip.',
      icon: BadgeRoundedIcon,
      tint: 'green',
    },
    {
      title: 'Priorities and service times',
      description: 'Urgent orders go first, with realistic time allowed at each stop.',
      icon: FlagRoundedIcon,
      tint: 'teal',
    },
    {
      title: 'Re-planning on demand',
      description: 'When orders change during the day, re-run the plan without starting over.',
      icon: AutorenewRoundedIcon,
      tint: 'red',
    },
  ] as IconItem[],
};

export const closing = {
  title: 'Ready to plan smarter delivery days?',
  lede: 'A short, no-obligation walkthrough of planning, review and live tracking.',
  // Shown on one line, with the "Schedule a demo" button as the last stop.
  steps: [
    { title: 'Book a 20-minute call', detail: 'Choose a weekday and time that suits you.' },
    { title: 'Tell us about your operation', detail: 'Fleet size, delivery windows and depots.' },
    { title: 'See it in action', detail: 'A planned day, reviewed and tracked end to end in ExPlan.' },
  ],
};
